import { useState } from "react";
import "../styles/welcome-surface.css";

/* These map the onboarding goal onto a plain-English description of what the
   numbers were sized for. Deliberately unglamorous — this screen is a footnote
   to the product, not its payoff. */
const GOAL_NOTES = {
  lose_fat: "sized a little under what you use in a day",
  build_muscle: "sized a little above what you use in a day",
  get_stronger: "sized to support strength work",
  feel_better: "sized to roughly match what you use in a day",
  train_event: "sized to roughly match what you use in a day",
};

export default function MacroResults({ macros, onConfirm, onBack }) {
  const day = macros.daily;
  const note = GOAL_NOTES[macros.goal] || GOAL_NOTES.feel_better;

  /* The screen used to name the disordered-eating risk in a footnote and then
     offer exactly one button — "Got it". Acknowledgement without an affordance
     is the least useful form of care, so declining is a real answer here: the
     numbers go away, and the way back is one press if that changes. */
  const [declined, setDeclined] = useState(false);

  /* No bars, no rings, no proportions. A ranked chart of macros would invite
     exactly the comparing this screen is trying to defuse — a plain list of
     five quiet lines is the whole design. */
  const rows = [
    { label: "Protein", value: `${day.protein}g`, color: "var(--macro-protein)" },
    { label: "Carbs", value: `${day.carbs}g`, color: "var(--macro-carbs)" },
    { label: "Fat", value: `${day.fat}g`, color: "var(--macro-fat)" },
  ];

  return (
    <main className="ws-screen" aria-label="Food guidance">
      <div className="ws-wash" aria-hidden="true" />

      <div className="ws-auth ws-enter">
        <div className="ws-auth-head">
          <p className="section-label">One last, quiet thing</p>
          <h1 className="ws-q">Somewhere gentle to start with food.</h1>
          <p className="ws-hint">
            Eating well helps you feel well. These are background numbers, not a
            test — nothing else here depends on them.
          </p>
        </div>

        {declined ? (
          <div className="ws-optout">
            <p>
              Left alone, then. Nothing in the check-in, the movement or the
              evidence needs these — the guide stays tucked away under Nutrition
              if you ever want to look.
            </p>
          </div>
        ) : (
          <>
            {/* Protein, carbs and fat lead and the calorie figure sits last.
                Every figure is set in the same quiet ink: the numbers were the
                highest-contrast text on a screen whose own copy calls them
                background, and singling out the kcal row left the grams still
                shouting. The three macro swatches are the colour key; Fiber and
                Energy keep the slot but not the dot, because a swatch that
                encodes nothing is decoration in a legend's clothes. */}
            <section className="ws-nutrition" aria-label="Daily food guide">
              {rows.map((m) => (
                <div className="ws-nutrition-row" key={m.label}>
                  <span className="ws-nutrition-key">
                    <span className="ws-dot" style={{ background: m.color }} aria-hidden="true" />
                    {m.label}
                  </span>
                  <span className="ws-nutrition-val">{m.value}</span>
                </div>
              ))}

              <div className="ws-nutrition-row">
                <span className="ws-nutrition-key">
                  <span className="ws-dot" aria-hidden="true" />
                  Fiber
                </span>
                <span className="ws-nutrition-val">{day.fiber}g</span>
              </div>

              <div className="ws-nutrition-row">
                <span className="ws-nutrition-key">
                  <span className="ws-dot" aria-hidden="true" />
                  Energy, roughly
                </span>
                <span className="ws-nutrition-val">{day.calories} kcal</span>
              </div>
            </section>

            <p className="ws-footnote">
              The same guide every day — {note}. No training-day or rest-day maths
              to keep track of, and you can change any of it in settings.
            </p>

            <p className="ws-footnote">
              If counting food isn’t good for you, leave it be. Checking in and
              moving are the parts that matter here.
            </p>
          </>
        )}
      </div>

      <div className="ws-actions">
        <button type="button" className="btn btn-primary btn-lg btn-full" onClick={onConfirm}>
          {declined ? "Take me in" : "Got it"}
        </button>

        {/* The lever the footnote above promises. Quiet, reversible, and it does
            the same safe thing either way — nothing is lost by declining. */}
        <button
          type="button"
          className="btn btn-quiet btn-lg btn-full"
          onClick={() => setDeclined((d) => !d)}
        >
          {declined ? "Actually, show me the numbers" : "I’d rather not track food"}
        </button>

        {onBack && (
          <div className="ws-auth-alt">
            <button type="button" className="ds-link" onClick={onBack}>
              Change my details first
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
